import { content } from "./content.js";
import * as conversation from "./conversation.js";
import * as email from "./email.js";
import panelStyles from "./styles.css";

// Panel is mounted inside a shadow root so host-page CSS cannot reach our
// internals. `shadowHostEl` is the light-DOM div attached to document.body
// that owns the shadow. `panelEl` is the .tp-panel <aside> inside the shadow;
// existing callers (conversation.js, email.js) keep using panelEl unchanged
// because querySelector works inside the shadow.
let shadowHostEl = null;
let panelEl = null;
let lastTrigger = null;

export function open(trigger) {
  if (!panelEl) {
    const built = create();
    shadowHostEl = built.host;
    panelEl = built.panel;
    document.body.appendChild(shadowHostEl);
    // Force a reflow so the initial transform is committed before the
    // open-state class is added, otherwise the slide-in animation is skipped.
    void panelEl.offsetHeight;
  }
  lastTrigger = trigger ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  panelEl.inert = false;
  panelEl.setAttribute("aria-hidden", "false");
  panelEl.classList.add("is-open");
  document.body.classList.add("tp-panel-open");
  panelEl.querySelector(".tp-panel__close").focus();
  document.addEventListener("keydown", onKeydown);
}

export function close() {
  if (!panelEl) return;
  // Preserve an active conversation (Panel 3) across close/reopen: once the user
  // has an answer in progress or completed, closing the slide-out should just
  // minimize it, and reopening should return them to that answer — not the
  // starting screen (which would also re-ask for the lead form). The stream is
  // not aborted and the DOM stays mounted, so an in-progress answer keeps filling
  // in while hidden. The earlier stages (question / lead form) still reset to a
  // clean start on close.
  const hasActiveConversation = panelEl.querySelector(".tp-conversation") !== null;
  if (!hasActiveConversation) {
    conversation.reset(panelEl);
    email.reset(panelEl);
    // The user may have X'd mid-trial-form; clear the page-1 form's pending lock
    // so the next open shows an interactive question form.
    const pending = panelEl.querySelector(".tp-question.is-pending");
    if (pending) pending.classList.remove("is-pending");
  }
  panelEl.classList.remove("is-open");
  document.body.classList.remove("tp-panel-open");
  panelEl.inert = true;
  panelEl.setAttribute("aria-hidden", "true");
  document.removeEventListener("keydown", onKeydown);
  if (lastTrigger && typeof lastTrigger.focus === "function") lastTrigger.focus();
}

function onKeydown(event) {
  if (event.key === "Escape") close();
}

function create() {
  // Shadow host: light-DOM div whose only job is to own the shadow root.
  // All our CSS targets elements INSIDE the shadow so host-page styles
  // cannot reach them.
  const host = document.createElement("div");
  host.className = "tp-shadow-host";
  // Reset any inherited properties (font, color, etc.) on the host element
  // itself so host-page styling never leaks across the shadow boundary's
  // few inheritable channels. Inherited custom properties still pass
  // through, which is what lets --tp-panel-width keep working.
  host.style.all = "initial";
  const shadow = host.attachShadow({ mode: "open" });

  const styleEl = document.createElement("style");
  styleEl.textContent = panelStyles;
  shadow.appendChild(styleEl);

  const panel = document.createElement("aside");
  panel.className = "tp-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", "Protégé");
  panel.setAttribute("aria-hidden", "true");
  panel.inert = true;
  panel.innerHTML = template();
  shadow.appendChild(panel);
  wire(panel);
  return { host, panel };
}

function template() {
  return `
    <header class="tp-panel__header">
      <div class="tp-panel__brand">${ICON_LOGO}<span>Protégé&trade;</span></div>
      <button class="tp-panel__close" type="button" aria-label="Close">${ICON_CLOSE}</button>
    </header>
    <div class="tp-panel__body">
      <h2 class="tp-panel__heading"></h2>
      <form class="tp-question" novalidate>
        <textarea class="tp-question__input" rows="1" autocomplete="off" maxlength="${content.maxCharacterLimit}"></textarea>
        <button class="tp-question__submit" type="submit" aria-label="Submit question">${ICON_ARROW}</button>
      </form>
      <p class="tp-question__count" role="status" hidden></p>
      <p class="tp-question__error" role="alert" hidden></p>
      <section class="tp-accordion is-open" data-section="examples">
        <button class="tp-accordion__toggle" type="button" aria-expanded="true">
          <span class="tp-accordion__label"></span>
          <span class="tp-accordion__chevron">${ICON_CHEVRON}</span>
        </button>
        <ul class="tp-examples"></ul>
      </section>
    </div>
    <footer class="tp-panel__footer">
      <span class="tp-panel__terms"></span>
    </footer>
  `;
}

function wire(root) {
  root.querySelector(".tp-panel__heading").textContent = content.heading;
  root.querySelector(".tp-question__input").placeholder = content.inputPlaceholder;
  root.querySelector('[data-section="examples"] .tp-accordion__label').textContent = content.examplesLabel;

  const list = root.querySelector(".tp-examples");
  for (const text of content.examples) {
    list.appendChild(buildExample(text, root));
  }

  const terms = root.querySelector(".tp-panel__terms");
  terms.append(document.createTextNode(content.termsPrefix));
  const link = document.createElement("a");
  link.href = content.termsLinkHref;
  link.textContent = content.termsLinkText;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  terms.appendChild(link);
  terms.append(document.createTextNode(content.termsSuffix));

  root.querySelector(".tp-panel__close").addEventListener("click", close);

  root.querySelectorAll(".tp-accordion").forEach((section) => {
    const toggle = section.querySelector(".tp-accordion__toggle");
    const body = section.querySelector(".tp-accordion__panel, .tp-examples");
    toggle.addEventListener("click", () => {
      const isOpen = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", String(!isOpen));
      section.classList.toggle("is-open", !isOpen);
      if (isOpen) body.setAttribute("hidden", "");
      else body.removeAttribute("hidden");
    });
  });

  const questionForm = root.querySelector(".tp-question");
  const questionInput = root.querySelector(".tp-question__input");
  const questionCount = root.querySelector(".tp-question__count");

  // Auto-grow the textarea as the user types. CSS `max-height` caps the
  // growth; `overflow-y: auto` engages once content exceeds the cap.
  const autoResize = () => {
    questionInput.style.height = "auto";
    questionInput.style.height = `${questionInput.scrollHeight}px`;
  };
  questionInput.addEventListener("input", autoResize);

  // Character counter mirroring the AiPromptBar composer. `maxlength` already
  // hard-caps the input; this surfaces the running count once the user nears
  // the cap and swaps to a max-reached message at the limit.
  const updateCharacterCount = () => {
    const count = questionInput.value.length;
    const { maxCharacterLimit: max, showCharacterCountLimit: threshold } = content;
    if (count <= threshold) {
      questionCount.hidden = true;
      questionCount.textContent = "";
      questionCount.classList.remove("is-max");
      questionCount.setAttribute("role", "status");
      return;
    }
    const atMax = count >= max;
    questionCount.hidden = false;
    questionCount.classList.toggle("is-max", atMax);
    questionCount.setAttribute("role", atMax ? "alert" : "status");
    questionCount.textContent = atMax
      ? `${content.maxCharacterLimitMessage} ${count}/${max}`
      : `${count}/${max}`;
  };
  questionInput.addEventListener("input", updateCharacterCount);

  // Enter submits, Shift+Enter inserts a newline (standard chat-input
  // convention). Without this, Enter would insert a newline in the textarea
  // and the form would never submit from the keyboard.
  questionInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      questionForm.requestSubmit();
    }
  });

  questionForm.addEventListener("submit", (event) => {
    event.preventDefault();
    submitQuestion(questionInput.value.trim(), root);
  });
}

function submitQuestion(value, root) {
  if (!value) return;
  const form = root.querySelector(".tp-question");
  if (form.classList.contains("is-pending")) return;
  clearQuestionError(root);
  form.classList.add("is-pending");
  startConversation(value, root);
}

// DEV ONLY: skip the hosted Panel 2 lead form when running on localhost so the
// conversation flow (Panel 1 → Panel 3) can be exercised without the trial form /
// unlock token. In local dev the peer authenticates via its fallback WAM user, so
// Panel 3 works with no bearer token. Never true on the deployed cert/prod hosts.
const SKIP_PANEL_2 =
  typeof window !== "undefined" &&
  !!window.location &&
  /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);

function startConversation(question, root) {
  const form = root.querySelector(".tp-question");

  // DEV bypass: go straight to Panel 3 with no unlock token (see SKIP_PANEL_2).
  if (SKIP_PANEL_2) {
    form.classList.remove("is-pending");
    conversation.show(panelEl, { question, token: null });
    return;
  }

  // panel.js remains the flow coordinator: Panel 1 collects the question,
  // email.js mounts the hosted Panel 2 form, and this callback hands the
  // returned unlock token to Panel 3.
  email.show(panelEl, {
    question,
    onSubmit: ({ formData }) => {
      form.classList.remove("is-pending");

      const token = formData?.unlockToken;
      if (!token) {
        showQuestionError(root, content.submitErrorMessage);
        return;
      }

      // Keep the token in memory and pass it directly to Panel 3.
      conversation.show(panelEl, { question, token });
    },
    onBack: () => {
      form.classList.remove("is-pending");
    },
  });
}

function showQuestionError(root, message) {
  const errorEl = root.querySelector(".tp-question__error");
  if (!errorEl) return;
  errorEl.textContent = message;
  errorEl.removeAttribute("hidden");
}

function clearQuestionError(root) {
  const errorEl = root.querySelector(".tp-question__error");
  if (!errorEl) return;
  errorEl.textContent = "";
  errorEl.setAttribute("hidden", "");
}

function buildExample(text, root) {
  const li = document.createElement("li");
  li.className = "tp-example";
  const textBtn = document.createElement("button");
  textBtn.type = "button";
  textBtn.className = "tp-example__text";
  textBtn.textContent = text;
  textBtn.addEventListener("click", () => {
    // Populate the question input only — don't auto-advance. The user clicks
    // the submit arrow (or presses Enter) to proceed to the next screen.
    const input = root.querySelector(".tp-question__input");
    input.value = text;
    input.focus();
  });
  const copy = document.createElement("button");
  copy.type = "button";
  copy.className = "tp-example__copy";
  copy.setAttribute("aria-label", "Copy question");
  copy.innerHTML = ICON_COPY;
  copy.addEventListener("click", () => copyToClipboard(text, copy));
  li.appendChild(textBtn);
  li.appendChild(copy);
  return li;
}

async function copyToClipboard(text, button) {
  try {
    await navigator.clipboard.writeText(text);
    button.classList.add("is-copied");
    setTimeout(() => button.classList.remove("is-copied"), 1200);
  } catch (err) {
    console.warn("[TryProtege] clipboard write failed", err);
  }
}

// LexisNexis "knowledge burst" mark, lifted from ln-design-system's
// logo.component.ts (src/components/logo). fill="currentColor" so it inherits
// the brand text color; the Protégé™ wordmark beside it provides the label, so
// the glyph is decorative (aria-hidden).
const ICON_LOGO = `<svg class="tp-panel__logo" viewBox="0 0 33 32" xmlns="http://www.w3.org/2000/svg" fill="currentColor" aria-hidden="true"><path d="M12.9053 32.0007C7.44733 30.9667 6.32933 25.7237 6.08133 22.8777C5.62633 16.6467 9.16233 9.80966 15.2943 7.07066C15.3015 7.06655 15.31 7.06545 15.318 7.06761C15.3259 7.06977 15.3327 7.075 15.3368 7.08216C15.3409 7.08932 15.342 7.09782 15.3399 7.10579C15.3377 7.11376 15.3325 7.12055 15.3253 7.12466C14.7825 7.53892 14.2661 7.9868 13.7793 8.46566C11.0906 11.1155 9.31613 14.5531 8.71334 18.2797C8.40726 20.1885 8.40726 22.1339 8.71334 24.0427C9.10633 26.7877 10.6873 30.3997 12.9333 31.9427C12.9389 31.9466 12.943 31.9522 12.9451 31.9587C12.9472 31.9652 12.9472 31.9722 12.9451 31.9786C12.943 31.9851 12.9389 31.9907 12.9333 31.9947C12.9278 31.9986 12.9211 32.0007 12.9143 32.0007H12.9053ZM18.8283 5.92866C18.9433 6.00266 19.2353 6.20366 19.4283 6.34966C20.1111 6.86147 20.747 7.43306 21.3283 8.05766C21.9072 8.68383 22.428 9.36128 22.8843 10.0817C23.3404 10.8018 23.7293 11.5624 24.0463 12.3537C24.0609 12.3897 24.0844 12.4215 24.1145 12.4462C24.1446 12.4708 24.1804 12.4875 24.2186 12.4947C24.2569 12.5018 24.2963 12.4993 24.3333 12.4872C24.3703 12.4752 24.4037 12.454 24.4303 12.4257C24.4587 12.3948 24.4777 12.3565 24.4852 12.3153C24.4926 12.274 24.4882 12.2315 24.4723 12.1927V12.1847C23.851 10.524 22.932 8.99054 21.7603 7.65966C21.6433 7.52666 21.5233 7.39566 21.4003 7.26566C21.3951 7.25974 21.3921 7.25212 21.392 7.2442C21.3919 7.23628 21.3946 7.22859 21.3997 7.22252C21.4048 7.21645 21.4119 7.21242 21.4198 7.21116C21.4276 7.20991 21.4356 7.2115 21.4423 7.21566C21.8717 7.47998 22.2866 7.76705 22.6853 8.07566C23.3571 8.59989 23.9827 9.18068 24.5553 9.81166C24.5815 9.83995 24.6143 9.86125 24.6508 9.87361C24.6873 9.88598 24.7263 9.88902 24.7643 9.88246C24.8022 9.8759 24.838 9.85995 24.8682 9.83605C24.8984 9.81216 24.9222 9.78109 24.9373 9.74566C24.9539 9.70721 24.9593 9.66487 24.953 9.62349C24.9466 9.58211 24.9287 9.54335 24.9013 9.51166L24.8923 9.50066C24.3221 8.82729 23.6958 8.2034 23.0203 7.63566C22.5084 7.21074 21.9689 6.82022 21.4053 6.46666C21.3975 6.46321 21.3914 6.4568 21.3883 6.44883C21.3852 6.44086 21.3854 6.43198 21.3888 6.42416C21.3923 6.41634 21.3987 6.4102 21.4067 6.40711C21.4146 6.40401 21.4235 6.40421 21.4313 6.40766C21.7763 6.50766 22.1183 6.60766 22.4583 6.72866C23.2611 7.01444 24.039 7.36561 24.7843 7.77866C24.818 7.79665 24.8556 7.80603 24.8937 7.80596C24.9319 7.80588 24.9694 7.79636 25.003 7.77823C25.0366 7.7601 25.0651 7.73394 25.0861 7.70209C25.1071 7.67023 25.1199 7.63366 25.1233 7.59566C25.1265 7.55412 25.118 7.51252 25.0987 7.47558C25.0794 7.43864 25.0502 7.40784 25.0143 7.38666L25.0053 7.38066C24.2541 6.91697 23.4661 6.51563 22.6493 6.18066C22.2153 6.00566 21.7723 5.84666 21.3203 5.70866C21.313 5.70612 21.3067 5.70114 21.3026 5.69455C21.2985 5.68796 21.2967 5.68015 21.2977 5.67243C21.2986 5.66471 21.3022 5.65754 21.3077 5.65212C21.3133 5.64671 21.3206 5.64337 21.3283 5.64266C22.2935 5.6119 23.2595 5.6688 24.2143 5.81266C24.2493 5.81778 24.2849 5.81471 24.3185 5.8037C24.352 5.79269 24.3825 5.77404 24.4076 5.74922C24.4328 5.7244 24.4518 5.69409 24.4632 5.66068C24.4746 5.62726 24.478 5.59165 24.4733 5.55666C24.4677 5.50802 24.4467 5.46243 24.4135 5.42648C24.3802 5.39054 24.3364 5.36608 24.2883 5.35666H24.2823C23.1898 5.12718 22.0767 5.00991 20.9603 5.00666C20.9526 5.00645 20.9452 5.00363 20.9394 4.99867C20.9335 4.9937 20.9294 4.98689 20.9279 4.97934C20.9264 4.97179 20.9275 4.96395 20.931 4.95711C20.9346 4.95026 20.9403 4.94482 20.9473 4.94166C21.7392 4.64211 22.553 4.40374 23.3813 4.22866C23.4131 4.22187 23.4431 4.20836 23.4693 4.18903C23.4954 4.1697 23.5171 4.14499 23.533 4.11658C23.5488 4.08817 23.5583 4.0567 23.561 4.02428C23.5636 3.99187 23.5593 3.95927 23.5483 3.92866C23.5289 3.87866 23.4928 3.83688 23.4462 3.8103C23.3996 3.78372 23.3453 3.77396 23.2923 3.78266C21.7355 4.00045 20.2166 4.43395 18.7793 5.07066C18.3903 4.81666 17.9873 4.57966 17.5793 4.36266C15.9801 3.52102 14.2393 2.98166 12.4443 2.77166C10.7383 2.57166 7.84433 2.54166 5.74433 3.74666C5.73757 3.75183 5.72903 3.75411 5.72059 3.75298C5.71215 3.75185 5.70451 3.74742 5.69933 3.74066C5.69416 3.7339 5.69189 3.72536 5.69301 3.71692C5.69414 3.70848 5.69857 3.70083 5.70533 3.69566C8.21974 1.6357 11.2912 0.372213 14.5273 0.0666603C18.5929 -0.303794 22.6478 0.879343 25.8762 3.37798C29.1046 5.87662 31.2668 9.5053 31.9277 13.5338C32.5886 17.5623 31.6991 21.6916 29.4383 25.0908C27.1775 28.49 23.7131 30.9067 19.7423 31.8547C19.5038 31.8799 19.2642 31.8925 19.0243 31.8927C9.90533 31.8917 5.84033 13.1707 18.8283 5.92866ZM6.03433 28.8537C-1.79467 22.6147 3.23433 8.03866 14.9603 6.06466C14.9683 6.06214 14.9769 6.06289 14.9843 6.06673C14.9917 6.07057 14.9973 6.0772 14.9998 6.08516C15.0024 6.09312 15.0016 6.10175 14.9978 6.10916C14.9939 6.11656 14.9873 6.12214 14.9793 6.12466C14.5793 6.27866 14.1887 6.44833 13.8073 6.63366C10.436 8.27132 7.69868 10.9749 6.01933 14.3257C2.26133 21.8727 4.94333 27.2497 6.08133 28.8097C6.08477 28.8145 6.08682 28.8202 6.08727 28.8262C6.08772 28.8321 6.08655 28.8381 6.08388 28.8434C6.08121 28.8488 6.07715 28.8533 6.07212 28.8565C6.0671 28.8597 6.0613 28.8615 6.05533 28.8617C6.04761 28.8615 6.04018 28.8587 6.03433 28.8537ZM0.840335 21.7157C0.672335 21.2327 0.422335 20.4707 0.333335 20.1007C-0.0643993 18.5163 -0.106767 16.8636 0.209267 15.261C0.525301 13.6583 1.19191 12.1454 2.16133 10.8307C2.17233 10.8167 2.18033 10.8027 2.19233 10.7887C3.22046 9.42444 4.48232 8.25331 5.91933 7.32966C7.35488 6.40723 8.94081 5.74321 10.6053 5.36766C12.0006 5.04346 13.4375 4.93653 14.8653 5.05066C14.8727 5.05198 14.8794 5.05586 14.8842 5.06162C14.8891 5.06738 14.8917 5.07465 14.8917 5.08216C14.8917 5.08967 14.8891 5.09694 14.8842 5.1027C14.8794 5.10846 14.8727 5.11234 14.8653 5.11366C12.5029 5.40977 10.2328 6.21456 8.21138 7.47257C6.18997 8.73058 4.46534 10.4119 3.15633 12.4007C1.34641 15.1442 0.550331 18.4332 0.905335 21.7007C0.905944 21.7051 0.905554 21.7096 0.904191 21.7139C0.902829 21.7181 0.900529 21.722 0.89746 21.7253C0.894392 21.7286 0.890632 21.7311 0.886458 21.7327C0.882283 21.7343 0.877799 21.735 0.873335 21.7347C0.866504 21.7356 0.85955 21.7343 0.853573 21.7308C0.847597 21.7274 0.842938 21.722 0.840335 21.7157ZM1.07933 9.96866C1.65783 8.41865 2.58347 7.02163 3.78533 5.88466C6.45133 3.73266 10.8133 2.90966 15.0053 4.13466C15.0137 4.13638 15.021 4.14136 15.0257 4.14848C15.0304 4.15561 15.0321 4.16431 15.0303 4.17266C15.0286 4.18101 15.0236 4.18834 15.0165 4.19303C15.0094 4.19772 15.0007 4.19938 14.9923 4.19766C12.3783 3.88906 9.72897 4.26117 7.30113 5.2779C4.87329 6.29462 2.74937 7.92146 1.13533 10.0007C1.13244 10.005 1.12859 10.0086 1.12408 10.0112C1.11957 10.0138 1.11453 10.0153 1.10933 10.0157C1.10377 10.0157 1.09829 10.0143 1.09341 10.0116C1.08853 10.009 1.08439 10.0051 1.0814 10.0004C1.07841 9.99572 1.07665 9.99036 1.07629 9.9848C1.07593 9.97925 1.07697 9.9737 1.07933 9.96866Z" /></svg>`;
const ICON_CLOSE = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M6 6l12 12M18 6L6 18"/></svg>`;
const ICON_ARROW = `<svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true"><circle cx="12" cy="12" r="10.5" fill="none" stroke="currentColor" stroke-width="1.25"/><path fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M10.5 8.5l3.5 3.5-3.5 3.5"/></svg>`;
const ICON_CHEVRON = `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M6 9l6 6 6-6"/></svg>`;
const ICON_COPY = `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><rect x="8" y="8" width="11" height="13" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><path fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" d="M16 8V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v11"/></svg>`;
